import React, { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { useI18n } from "@/lib/i18n";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";

export default function ResetPassword() {
  const { t } = useI18n();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (password !== confirm) return toast.error(t("passwords_mismatch"));
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success(t("password_updated"));
      nav("/login");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
    setLoading(false);
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background animated-texture grain-overlay p-6" data-testid="reset-password-page">
      <Card className="w-full max-w-md border-border">
        <CardContent className="p-6 space-y-5">
          <div className="flex items-center gap-3">
            <img src="/logo-agrinova.png" alt="AgriNova" className="w-10 h-10 rounded-lg object-cover" />
            <div>
              <div className="font-bold">AgriNova</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{t("slogan")}</div>
            </div>
          </div>
          <h1 className="text-2xl font-bold tracking-tight">{t("reset_password_title")}</h1>
          {!token ? (
            <p className="text-sm text-destructive" data-testid="reset-no-token">{t("invalid_reset_link")}</p>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="reset-password-form">
              <div className="space-y-1.5">
                <Label htmlFor="new-password">{t("new_password")}</Label>
                <Input id="new-password" type="password" minLength={8} required data-testid="reset-new-password"
                  value={password} onChange={(e) => setPassword(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="confirm-password">{t("confirm_password")}</Label>
                <Input id="confirm-password" type="password" minLength={8} required data-testid="reset-confirm-password"
                  value={confirm} onChange={(e) => setConfirm(e.target.value)} />
              </div>
              <Button type="submit" className="w-full" disabled={loading} data-testid="reset-submit">
                {loading ? "…" : t("reset_password_title")}
              </Button>
            </form>
          )}
          <Link to="/login" className="block text-center text-sm text-primary hover:underline" data-testid="back-to-login">
            {t("back_to_login")}
          </Link>
        </CardContent>
      </Card>
    </div>
  );
}
